import { ReactNode } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    addMonths,
    addWeeks,
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    startOfDay
} from 'date-fns';
import { FormattedMessage } from 'react-intl';

type TPresetDate = { key: string; label: ReactNode; getDate: () => Date };

export const PRESET_DATES: TPresetDate[] = [
    {
        key: '4-weeks',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.four_weeks"
                defaultMessage="4 weeks"
            />
        ),
        getDate: () => addWeeks(new Date(), 4)
    },
    {
        key: '6-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.six_months"
                defaultMessage="6 months"
            />
        ),
        getDate: () => addMonths(new Date(), 6)
    },
    {
        key: '9-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.nine_months"
                defaultMessage="9 months"
            />
        ),
        getDate: () => addMonths(new Date(), 9)
    }
];

export const getOfferDurationPreset = (
    selectedOffer: TFormalOffer
): TPresetDate => {
    const durationMonths = getDurationInMonths(selectedOffer.duration!);
    return {
        key: 'full-duration',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.full_duration"
                defaultMessage="{months} months"
                values={{ months: durationMonths }}
            />
        ),
        getDate: () => addMonths(new Date(), durationMonths)
    };
};

const LOCALE = { UK: 'en-GB', DE: 'de-DE' } as const;
const CONJUNCTION = { UK: 'and', DE: 'und' } as const;

const formatUnit = (value: number, unit: 'month' | 'week' | 'day') =>
    new Intl.NumberFormat(LOCALE[getCountry()], {
        style: 'unit',
        unit,
        unitDisplay: 'long'
    }).format(value);

export const formatDurationFromDate = (selectedDate: string): string => {
    const today = startOfDay(new Date());
    const target = new Date(selectedDate);
    const totalDays = differenceInDays(target, today);

    if (totalDays < 1) return formatUnit(0, 'day');

    const months = differenceInMonths(target, today);

    if (months >= 1) {
        const dateAfterMonths = addMonths(today, months);
        const remainingDays = differenceInDays(target, dateAfterMonths);

        if (remainingDays === 0) {
            return formatUnit(months, 'month');
        }
        return `${formatUnit(months, 'month')} ${CONJUNCTION[getCountry()]} ${formatUnit(remainingDays, 'day')}`;
    }

    const weeks = differenceInWeeks(target, today);

    if (weeks >= 1) {
        const dateAfterWeeks = addWeeks(today, weeks);
        const remainingDays = differenceInDays(target, dateAfterWeeks);

        if (remainingDays === 0) {
            return formatUnit(weeks, 'week');
        }
        return `${formatUnit(weeks, 'week')} ${CONJUNCTION[getCountry()]} ${formatUnit(remainingDays, 'day')}`;
    }

    return formatUnit(totalDays, 'day');
};
